import { isBye } from "@/utils/bye";

/**
 * A "skipped" poll is one that nobody ever voted in, long after the match
 * finished. Coaches often skip voting in practice — e.g. a gala day with three
 * short games where a single vote round happens at the end. Those empty polls
 * shouldn't look like a failure, and shouldn't drag players into the
 * "Overlooked" list in cohesion analytics.
 *
 * Derived at read time only: no database column, no cron. If a vote ever
 * arrives, the poll stops being skipped automatically.
 */

/** Grace period after kickoff before an empty poll is considered skipped. */
export const SKIPPED_POLL_GRACE_MS = 24 * 60 * 60 * 1000;

export interface SkippableGame {
  date: string;
  match_time?: string | null;
  status?: string | null;
  opponent?: string | null;
}

/**
 * Kickoff timestamp for a game. When no kick-off time is recorded we fall back
 * to end-of-day so the grace period is never applied too early.
 */
export function pollKickoffTime(
  date: string,
  matchTime?: string | null
): number {
  if (!date) return NaN;
  if (matchTime) {
    const t = new Date(`${date}T${matchTime}`).getTime();
    if (!isNaN(t)) return t;
  }
  const eod = new Date(`${date}T23:59:59`).getTime();
  return eod;
}

/**
 * True when the match kicked off (and finished) at least 24 hours ago and no
 * votes were ever recorded. Byes and cancelled matches are never "skipped" —
 * they have their own states.
 */
export function isSkippedPoll(
  game: SkippableGame | null | undefined,
  voteCount: number,
  now: number = Date.now()
): boolean {
  if (!game) return false;
  if (voteCount > 0) return false;
  if (isBye(game)) return false;
  if (game.status === "cancelled") return false;

  const kickoff = pollKickoffTime(game.date, game.match_time);
  if (isNaN(kickoff)) return false;

  return now >= kickoff + SKIPPED_POLL_GRACE_MS;
}
